// Modern Recent Sales Component
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../services/firebase';
import { startDirect } from '../../utils/messageUtils';
import { COLORS, LAYOUT, TYPOGRAPHY } from '../../constants';

const RecentSales = ({ sales = [] }) => {
  const navigate = useNavigate();
  const [buyerNames, setBuyerNames] = useState({});
  const [hoveredButton, setHoveredButton] = useState(null);

  // Handle contacting buyer
  const handleContactBuyer = async (sale) => {
    if (!sale.buyerId) {
      alert('Buyer information not available for this sale.');
      return;
    }

    if (sale.buyerId === auth.currentUser?.uid) {
      alert('You cannot message yourself.');
      return;
    }

    try {
      // Create or get existing chat thread
      const chatId = await startDirect(sale.buyerId, auth.currentUser.uid, sale.listingId);
      navigate(`/direct/${chatId}`);
    } catch (error) {
      alert('Unable to start conversation. Please try again.');
    }
  };

  // Fetch buyer names for all sales
  useEffect(() => {
    const fetchBuyerNames = async () => {
      const names = {};
      for (const sale of sales) {
        if (sale.buyerName) {
          names[sale.id] = sale.buyerName;
        } else if (sale.buyerId) {
          try {
            const userDoc = await getDoc(doc(db, 'users', sale.buyerId));
            if (userDoc.exists()) {
              names[sale.id] = userDoc.data().displayName || userDoc.data().email || 'Anonymous Buyer';
            } else {
              names[sale.id] = 'Anonymous Buyer';
            }
          } catch (error) {
            names[sale.id] = 'Anonymous Buyer';
          }
        } else {
          names[sale.id] = 'Anonymous Buyer';
        }
      }
      setBuyerNames(names);
    };

    if (sales.length > 0) {
      fetchBuyerNames();
    }
  }, [sales]);

  if (sales.length === 0) {
    return (
      <div style={modernStyles.section}>
        <div style={modernStyles.sectionHeader}>
          <h2 style={modernStyles.sectionTitle}>
            <span style={modernStyles.titleIcon}>
              <svg fill="currentColor" viewBox="0 0 24 24" style={{ width: '24px', height: '24px' }}>
                <path d="M17,18C15.89,18 15,18.89 15,20A2,2 0 0,0 17,22A2,2 0 0,0 19,20C19,18.89 18.1,18 17,18M1,2V4H3L6.6,11.59L5.24,14.04C5.09,14.32 5,14.65 5,15A2,2 0 0,0 7,17H19V15H7.42A0.25,0.25 0 0,1 7.17,14.75C7.17,14.7 7.18,14.66 7.2,14.63L8.1,13H15.55C16.3,13 16.96,12.58 17.3,11.97L20.88,5H5.21L4.27,3H1M7,18C5.89,18 5,18.89 5,20A2,2 0 0,0 7,22A2,2 0 0,0 9,20C9,18.89 8.1,18 7,18Z"/>
              </svg>
            </span>
            Recent Sales
          </h2>
          <p style={modernStyles.sectionSubtitle}>
            Track your latest transactions and performance
          </p>
        </div>
        
        <div style={modernStyles.emptyStateCard}>
          <div style={modernStyles.emptyStateIcon}>
            <svg fill="currentColor" viewBox="0 0 24 24" style={{ width: '48px', height: '48px' }}>
              <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z"/>
            </svg>
          </div>
          <h3 style={modernStyles.emptyStateTitle}>No Sales Yet</h3>
          <p style={modernStyles.emptyStateText}>
            Your sales history will appear here once you complete your first transaction. 
            Keep your listings active and engaging to attract buyers!
          </p>
          
          <div style={modernStyles.emptyStateActions}>
            <Link to="/add-listing" style={modernStyles.primaryButton}>
              <span style={modernStyles.buttonIcon}>
                <svg fill="currentColor" viewBox="0 0 24 24" style={{ width: '16px', height: '16px' }}>
                  <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                </svg>
              </span>
              Create Listing
            </Link>
            <Link to="/market" style={modernStyles.secondaryButton}>
              <span style={modernStyles.buttonIcon}>
                <svg fill="currentColor" viewBox="0 0 24 24" style={{ width: '16px', height: '16px' }}>
                  <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
                </svg>
              </span>
              Browse Market
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={modernStyles.section}>
      <div style={modernStyles.sectionHeader}>
        <h2 style={modernStyles.sectionTitle}>
          <span style={modernStyles.titleIcon}>
            <svg fill="currentColor" viewBox="0 0 24 24" style={{ width: '24px', height: '24px' }}>
              <path d="M17,18C15.89,18 15,18.89 15,20A2,2 0 0,0 17,22A2,2 0 0,0 19,20C19,18.89 18.1,18 17,18M1,2V4H3L6.6,11.59L5.24,14.04C5.09,14.32 5,14.65 5,15A2,2 0 0,0 7,17H19V15H7.42A0.25,0.25 0 0,1 7.17,14.75C7.17,14.7 7.18,14.66 7.2,14.63L8.1,13H15.55C16.3,13 16.96,12.58 17.3,11.97L20.88,5H5.21L4.27,3H1M7,18C5.89,18 5,18.89 5,20A2,2 0 0,0 7,22A2,2 0 0,0 9,20C9,18.89 8.1,18 7,18Z"/>
            </svg>
          </span>
          Recent Sales
        </h2>
        <p style={modernStyles.sectionSubtitle}>
          Your latest transactions and earnings
        </p>
        
        <Link to="/transactions" style={modernStyles.viewAllLink}>
          View All Transactions →
        </Link>
      </div>
      
      <div style={modernStyles.salesContainer}>
        {sales.map((sale, index) => (
          <div key={sale.id} style={modernStyles.saleCard}>
            <div style={modernStyles.saleHeader}>
              <div style={modernStyles.saleDate}>
                {new Date(sale.createdAt?.seconds ? sale.createdAt.seconds * 1000 : sale.createdAt).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
              <div style={modernStyles.saleStatusBadge}>
                <div style={modernStyles.statusDot}></div>
                <span>Completed</span>
              </div>
            </div>
            
            <div style={modernStyles.saleContent}>
              <div style={modernStyles.saleInfo}>
                <h4 style={modernStyles.saleTitle}>
                  {sale.listingTitle || sale.itemTitle || 'Unknown Item'}
                </h4>
                <p style={modernStyles.saleBuyer}>
                  Sold to {buyerNames[sale.id] || 'Loading...'}
                </p>
              </div>
              
              <div style={modernStyles.saleAmountContainer}>
                <div style={modernStyles.saleAmount}>
                  ${((sale.amount || 0) / 100).toFixed(2)}
                </div>
                <div style={modernStyles.saleProfit}>
                  +${((sale.sellerAmount || sale.amount * 0.95 || 0) / 100).toFixed(2)} profit
                </div>
              </div>
            </div>
            
            <div style={modernStyles.saleFooter}>
              <div style={modernStyles.saleProgress}>
                <div style={{
                  ...modernStyles.progressFill,
                  width: '100%',
                }}></div>
              </div>
              <div style={modernStyles.saleActions}>
                <button 
                  style={{
                    ...modernStyles.actionButton,
                    ...(hoveredButton === `details-${sale.id}` ? modernStyles.actionButtonHover : {})
                  }}
                  onMouseEnter={() => setHoveredButton(`details-${sale.id}`)}
                  onMouseLeave={() => setHoveredButton(null)}
                  onClick={() => navigate(`/listing/${sale.listingId}`)}
                >
                  View Details
                </button>
                <button 
                  style={{
                    ...modernStyles.actionButton,
                    ...(hoveredButton === `contact-${sale.id}` ? modernStyles.actionButtonHover : {})
                  }}
                  onMouseEnter={() => setHoveredButton(`contact-${sale.id}`)}
                  onMouseLeave={() => setHoveredButton(null)}
                  onClick={() => handleContactBuyer(sale)}
                >
                  Contact Buyer
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {sales.length >= 3 && (
        <div style={modernStyles.loadMoreContainer}>
          <Link to="/transactions" style={modernStyles.loadMoreButton}>
            <span>View All {sales.length > 5 ? '20+' : sales.length} Transactions</span>
            <span style={modernStyles.loadMoreArrow}>→</span>
          </Link>
        </div>
      )}
    </div>
  );
};

const modernStyles = {
  section: {
    marginBottom: LAYOUT.spacing.xxl,
  },
  
  sectionHeader: {
    marginBottom: LAYOUT.spacing.xl,
    textAlign: 'center',
    position: 'relative',
  },
  
  sectionTitle: {
    ...TYPOGRAPHY.heading2,
    color: COLORS.text.primary,
    fontSize: '2.5rem',
    fontWeight: '700',
    marginBottom: LAYOUT.spacing.sm,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: LAYOUT.spacing.md,
  },
  
  titleIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: '8px',
    color: COLORS.primary,
  },
  
  sectionSubtitle: {
    fontSize: '1.1rem',
    color: COLORS.text.muted,
    maxWidth: '500px',
    margin: '0 auto',
    lineHeight: '1.6',
    marginBottom: LAYOUT.spacing.md,
  },
  
  viewAllLink: {
    color: COLORS.primary,
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    ':hover': {
      color: COLORS.primaryDark,
    },
  },
  
  emptyStateCard: {
    backgroundColor: 'white',
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xxl,
    textAlign: 'center',
    border: '2px dashed rgba(0, 0, 0, 0.1)',
  },
  
  emptyStateIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '80px',
    height: '80px',
    backgroundColor: `${COLORS.primary}15`,
    borderRadius: '50%',
    margin: '0 auto',
    marginBottom: LAYOUT.spacing.lg,
    color: COLORS.primary,
  },
  
  emptyStateTitle: {
    ...TYPOGRAPHY.heading3,
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.md,
    fontSize: '1.8rem',
    fontWeight: '700',
  },
  
  emptyStateText: {
    fontSize: '1.1rem',
    color: COLORS.text.muted,
    lineHeight: '1.6',
    marginBottom: LAYOUT.spacing.xl,
    maxWidth: '600px',
    margin: `0 auto ${LAYOUT.spacing.xl}`,
  },
  
  emptyStateActions: {
    display: 'flex',
    gap: LAYOUT.spacing.md,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    backgroundColor: COLORS.primary,
    color: 'white',
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.xl}`,
    borderRadius: LAYOUT.borderRadius.lg,
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    ':hover': {
      backgroundColor: COLORS.primaryDark,
      transform: 'translateY(-2px)',
    },
  },
  
  secondaryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    backgroundColor: 'transparent',
    color: COLORS.primary,
    border: `2px solid ${COLORS.primary}`,
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.xl}`,
    borderRadius: LAYOUT.borderRadius.lg,
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    ':hover': {
      backgroundColor: COLORS.primary,
      color: 'white',
      transform: 'translateY(-2px)',
    },
  },
  
  buttonIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: '6px',
  },
  
  salesContainer: {
    display: 'grid',
    gap: LAYOUT.spacing.lg,
    maxWidth: '800px',
    margin: '0 auto',
  },
  
  saleCard: {
    backgroundColor: 'white',
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xl,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
  },
  
  saleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: LAYOUT.spacing.lg,
  },
  
  saleStatusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    color: '#15803d',
    padding: `${LAYOUT.spacing.xs} ${LAYOUT.spacing.sm}`,
    borderRadius: LAYOUT.borderRadius.full,
    fontSize: '0.85rem',
    fontWeight: '500',
  },
  
  statusDot: {
    width: '6px',
    height: '6px',
    backgroundColor: '#CE1126',
    borderRadius: '50%',
  },
  
  saleDate: {
    fontSize: '0.9rem',
    color: COLORS.text.muted,
    fontWeight: '500',
  },
  
  saleContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: LAYOUT.spacing.lg,
    gap: LAYOUT.spacing.md,
  },
  
  saleInfo: {
    flex: 1,
  },
  
  saleTitle: {
    fontSize: '1.3rem',
    fontWeight: '700',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.xs,
    lineHeight: '1.3',
  },
  
  saleBuyer: {
    fontSize: '0.95rem',
    color: COLORS.text.muted,
    margin: 0,
  },
  
  saleAmountContainer: {
    textAlign: 'right',
  },
  
  saleAmount: {
    fontSize: '1.8rem',
    fontWeight: '800',
    color: COLORS.primary,
    lineHeight: '1',
    marginBottom: LAYOUT.spacing.xs,
  },
  
  saleProfit: {
    fontSize: '0.85rem',
    color: '#CE1126',
    fontWeight: '600',
  },
  
  saleFooter: {
    borderTop: '1px solid rgba(0, 0, 0, 0.05)',
    paddingTop: LAYOUT.spacing.md,
  },
  
  saleProgress: {
    height: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    borderRadius: LAYOUT.borderRadius.full,
    marginBottom: LAYOUT.spacing.md,
    overflow: 'hidden',
  },
  
  progressFill: {
    height: '100%',
    backgroundColor: COLORS.primary,
    borderRadius: LAYOUT.borderRadius.full,
    transition: 'width 0.6s ease',
  },
  
  saleActions: {
    display: 'flex',
    gap: LAYOUT.spacing.sm,
    justifyContent: 'flex-end',
  },
  
  actionButton: {
    backgroundColor: 'transparent',
    border: `1px solid rgba(0, 0, 0, 0.2)`,
    color: COLORS.text.secondary,
    padding: `${LAYOUT.spacing.xs} ${LAYOUT.spacing.sm}`,
    borderRadius: LAYOUT.borderRadius.md,
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  
  actionButtonHover: {
    backgroundColor: COLORS.primary,
    color: 'white',
    borderColor: COLORS.primary,
    transform: 'translateY(-1px)',
  },
  
  loadMoreContainer: {
    textAlign: 'center',
    marginTop: LAYOUT.spacing.xl,
  },
  
  loadMoreButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: LAYOUT.spacing.sm,
    backgroundColor: 'white',
    color: COLORS.primary,
    border: `2px solid ${COLORS.primary}`,
    padding: `${LAYOUT.spacing.md} ${LAYOUT.spacing.xl}`,
    borderRadius: LAYOUT.borderRadius.lg,
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    ':hover': {
      backgroundColor: COLORS.primary,
      color: 'white',
      transform: 'translateY(-2px)',
    },
  },
  
  loadMoreArrow: {
    fontSize: '1.2rem',
    transition: 'transform 0.3s ease',
  },
};

export default RecentSales;